import { CUSTOMER_HOME_PATH, ROLES, isServiceProviderPortalRole } from "./constants";

const hasRole = (roles, role) => Boolean(roles?.includes?.(role));

const SERVICER_PROFILE_PATH = "/servicer-profile";

/**
 * Public-page "My Account" for an unauthenticated visitor with a resumable
 * signup in this browser (stored user id + step): ask before leaving for login,
 * since going to login clears the local signup data. Stale flags with no step
 * to resume do not prompt.
 */
export const shouldPromptIncompleteSignup = ({ isAuthenticated, resumeStep }) =>
  !isAuthenticated && resumeStep !== undefined && resumeStep !== null;

/**
 * Where the public-header "My Account" button should take the visitor.
 * Returns a route for an authenticated session, or null meaning "go to the
 * login page". Never derives a session from leftover signup data (stored
 * signup token / saved signup credentials): an unauthenticated visitor must
 * log in, even mid-signup — the login API sends pending customers to Resume
 * Signup itself.
 */
export const resolveMyAccountDestination = ({
  isAuthenticated,
  roles,
  isServiceProvider = false,
}) => {
  if (!isAuthenticated) {
    return null;
  }

  if (isServiceProvider) {
    return isServiceProviderPortalRole(roles) ? SERVICER_PROFILE_PATH : null;
  }

  if (
    hasRole(roles, ROLES.admin) ||
    hasRole(roles, ROLES.subAdmin) ||
    (!hasRole(roles, ROLES.serviceProvider) && hasRole(roles, ROLES.provider)) ||
    hasRole(roles, ROLES.AffiliateManager) ||
    hasRole(roles, ROLES.countyRecorder)
  ) {
    return "/admin-profile";
  }

  if (hasRole(roles, ROLES.customer) || hasRole(roles, ROLES.secondaryUser)) {
    return CUSTOMER_HOME_PATH;
  }

  if (isServiceProviderPortalRole(roles)) {
    return SERVICER_PROFILE_PATH;
  }

  if (hasRole(roles, ROLES.professional)) {
    return "/professional-profile";
  }

  return null;
};

/**
 * The servicer profile only exists on the servicer site. Off that site (the
 * main site or the CAR host) My Account must hand the session across instead
 * of navigating in-app to a route the current host does not have.
 */
export const myAccountOpensOnServicerSite = ({ destination, onServicerHost }) =>
  destination === SERVICER_PROFILE_PATH && !onServicerHost;
